/**
 * Quality Reports Dashboard - Enterprise Grade
 * Comprehensive quality reporting and analysis
 */

import React, { useState, useEffect } from 'react';
import {
  FileText, Download, Plus, Eye, Edit3, Search, Filter,
  CheckCircle, Clock, TrendingUp, Target, Calendar, UserCheck,
  Building, Award, AlertTriangle, BarChart3
} from 'lucide-react';
import { qualityReports } from '../../data/mockData';
import { useAppContext } from '../../context/AppContext';
import { useToast } from '../Common/ToastProvider';

const QualityReportsDashboard = () => {
  const { state } = useAppContext();
  const { showToast } = useToast();
  const selectedFacility = state.selectedFacility;
  
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadData();
  }, [selectedFacility]);

  useEffect(() => {
    filterData();
  }, [reports, filterStatus, filterCategory, searchTerm]);

  const loadData = () => {
    const facilityReports = qualityReports.filter(r => 
      r.facility.toLowerCase().includes(selectedFacility) || selectedFacility === 'all'
    );
    setReports(facilityReports);
  };

  const filterData = () => {
    let filtered = [...reports];
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(r => r.status.toLowerCase() === filterStatus.toLowerCase());
    }
    
    if (filterCategory !== 'all') {
      filtered = filtered.filter(r => r.category.toLowerCase() === filterCategory.toLowerCase());
    }
    
    if (searchTerm) {
      filtered = filtered.filter(r =>
        r.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.facility.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.assessor.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredReports(filtered);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Passed': return 'bg-green-100 text-green-800';
      case 'Under Review': return 'bg-yellow-100 text-yellow-800';
      case 'Failed': return 'bg-red-100 text-red-800';
      case 'Completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const stats = {
    total: reports.length,
    passed: reports.filter(r => r.status === 'Passed').length,
    underReview: reports.filter(r => r.status === 'Under Review').length,
    averageScore: reports.reduce((sum, r) => sum + r.score, 0) / reports.length || 0
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8" />
              Quality Reports
            </h1>
            <p className="text-emerald-100 text-lg">Comprehensive quality assessment and reporting</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowNewModal(true)}
              className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg font-semibold flex items-center gap-2 transition-all"
            >
              <Plus className="w-4 h-4" />
              New Report
            </button>
            <button
              onClick={() => setShowExportModal(true)}
              className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg font-semibold flex items-center gap-2 transition-all"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-emerald-100 rounded-xl">
              <FileText className="w-6 h-6 text-emerald-600" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-gray-800">{stats.total}</div>
              <div className="text-xs text-gray-500">Total Reports</div>
            </div>
          </div>
          <h3 className="font-bold text-gray-800">All Reports</h3>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-green-200">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-100 rounded-xl">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-green-600">{stats.passed}</div>
              <div className="text-xs text-gray-500">Passed</div>
            </div>
          </div>
          <h3 className="font-bold text-gray-800">Compliant</h3>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-yellow-200">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-yellow-100 rounded-xl">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-yellow-600">{stats.underReview}</div>
              <div className="text-xs text-gray-500">Under Review</div>
            </div>
          </div>
          <h3 className="font-bold text-gray-800">Pending</h3>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-blue-200">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <Award className="w-6 h-6 text-blue-600" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-blue-600">{stats.averageScore.toFixed(1)}%</div>
              <div className="text-xs text-gray-500">Avg Score</div>
            </div>
          </div>
          <h3 className="font-bold text-gray-800">Performance</h3>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search reports by title, facility, or assessor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="passed">Passed</option>
            <option value="under review">Under Review</option>
            <option value="failed">Failed</option>
            <option value="completed">Completed</option>
          </select>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          >
            <option value="all">All Categories</option>
            <option value="comprehensive">Comprehensive</option>
            <option value="medication">Medication</option>
            <option value="infection control">Infection Control</option>
            <option value="resident">Resident Care</option>
            <option value="satisfaction">Satisfaction</option>
          </select>
        </div>
      </div>

      {/* Reports List */}
      <div className="space-y-4">
        {filteredReports.map((report) => (
          <div key={report.id} className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200 hover:border-emerald-300 transition-all">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <FileText className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-800 mb-1">{report.title}</h3>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Building className="w-4 h-4" />
                        {report.facility}
                      </span>
                      <span className="flex items-center gap-1">
                        <UserCheck className="w-4 h-4" />
                        {report.assessor}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {report.date}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className={`text-3xl font-bold ${
                        report.score >= 95 ? 'text-green-600' :
                        report.score >= 85 ? 'text-blue-600' :
                        'text-yellow-600'
                      }`}>
                        {report.score}%
                      </div>
                      <div className="text-xs text-gray-500">Score</div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(report.status)}`}>
                      {report.status}
                    </span>
                  </div>
                </div>

                {report.metrics && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 p-4 bg-gray-50 rounded-lg">
                    {Object.entries(report.metrics).slice(0, 4).map(([key, value]) => (
                      <div key={key}>
                        <div className="text-xs text-gray-600 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                        <div className="text-lg font-bold text-gray-800">{value}{typeof value === 'number' && key.includes('Rate') ? '%' : ''}</div>
                      </div>
                    ))}
                  </div>
                )}

                {report.findings && report.findings.length > 0 && (
                  <div className="mt-4">
                    <h4 className="font-semibold text-gray-800 mb-2">Key Findings</h4>
                    <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                      {report.findings.slice(0, 3).map((finding, index) => (
                        <li key={index}>{finding}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2 ml-4">
                <button
                  onClick={() => setSelectedReport(report)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                  title="View Details"
                >
                  <Eye className="w-5 h-5" />
                </button>
                <button
                  onClick={() => showToast('Edit report feature coming soon', 'info')}
                  className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-all"
                  title="Edit"
                >
                  <Edit3 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => showToast('Downloading report...', 'info')}
                  className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all"
                  title="Download"
                >
                  <Download className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Report Detail Modal */}
      {selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-2xl p-6 max-w-3xl w-full mx-4 my-8 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">{selectedReport.title}</h2>
              <button
                onClick={() => setSelectedReport(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                  <div className="text-sm text-gray-600 mb-1">Score</div>
                  <div className="text-3xl font-bold text-emerald-600">{selectedReport.score}%</div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="text-sm text-gray-600 mb-1">Status</div>
                  <div className="text-lg font-bold text-blue-600">{selectedReport.status}</div>
                </div>
              </div>

              {selectedReport.metrics && (
                <div>
                  <h4 className="font-semibold mb-3">Metrics</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(selectedReport.metrics).map(([key, value]) => (
                      <div key={key} className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-xs text-gray-600 capitalize mb-1">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                        <div className="text-lg font-bold text-gray-800">{value}{typeof value === 'number' && key.includes('Rate') ? '%' : ''}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedReport.findings && (
                <div>
                  <h4 className="font-semibold mb-2">Findings</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 bg-green-50 p-4 rounded-lg">
                    {selectedReport.findings.map((finding, index) => (
                      <li key={index}>{finding}</li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedReport.recommendations && (
                <div>
                  <h4 className="font-semibold mb-2">Recommendations</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 bg-blue-50 p-4 rounded-lg">
                    {selectedReport.recommendations.map((rec, index) => (
                      <li key={index}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedReport.nextReview && (
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <div className="flex items-center gap-2 text-yellow-800">
                    <Calendar className="w-5 h-5" />
                    <span className="font-semibold">Next Review: {selectedReport.nextReview}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* New Report Modal */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-emerald-800 flex items-center gap-2">
                <Plus className="w-6 h-6 text-emerald-600" />
                Create Quality Report
              </h2>
              <button
                onClick={() => setShowNewModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Report Type</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500">
                    <option>Comprehensive Assessment</option>
                    <option>Medication Safety</option>
                    <option>Infection Control</option>
                    <option>Resident Care</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Facility</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500">
                    <option>Sunshine Gardens</option>
                    <option>Bayview Gardens</option>
                    <option>Green Hill Residence</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Report Title</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  placeholder="Enter report title..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 h-24"
                  placeholder="Enter report description..."
                ></textarea>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowNewModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    showToast('Quality report created successfully', 'success');
                    setShowNewModal(false);
                    loadData();
                  }}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold"
                >
                  Create Report
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <Download className="w-6 h-6 text-emerald-600" />
                Export Reports
              </h2>
              <button
                onClick={() => setShowExportModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => {
                  const csv = generateCSV();
                  downloadFile(csv, `quality-reports-${selectedFacility}.csv`, 'text/csv');
                  setShowExportModal(false);
                }}
                className="w-full px-4 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold flex items-center justify-center gap-2"
              >
                <FileText className="w-5 h-5" />
                Export as CSV
              </button>
              <button
                onClick={() => {
                  const json = JSON.stringify(reports, null, 2);
                  downloadFile(json, `quality-reports-${selectedFacility}.json`, 'application/json');
                  setShowExportModal(false);
                }}
                className="w-full px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold flex items-center justify-center gap-2"
              >
                <FileText className="w-5 h-5" />
                Export as JSON
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  function generateCSV() {
    const headers = ['ID', 'Title', 'Facility', 'Date', 'Score', 'Status', 'Category', 'Assessor'];
    const rows = reports.map(r => [
      r.id,
      r.title,
      r.facility,
      r.date,
      r.score,
      r.status,
      r.category,
      r.assessor
    ].join(','));
    return [headers.join(','), ...rows].join('\n');
  }

  function downloadFile(content, filename, mimeType) {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

export default QualityReportsDashboard;
